import { useAppStore } from '../store/app-store.js';
import { toDate, type DateLike } from './parse.js';

export function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  } catch {
    return 'UTC';
  }
}

export function getResolvedTimezone(): string {
  try {
    const fromStore = useAppStore.getState().timezone;
    if (fromStore) return fromStore;
  } catch {
    // store may not be initialised in some non-browser contexts
  }
  return detectBrowserTimezone();
}

export function getResolvedLocale(): string {
  try {
    const fromStore = useAppStore.getState().locale;
    if (fromStore) return fromStore;
  } catch {
    // ignore
  }
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return 'en';
}

export function toLocalDate(input: DateLike, tz?: string): Date {
  // Convert a UTC instant to a `Date` whose getXxx() returns local-tz components.
  // This is a representation hack used by date pickers and formatters: the
  // resulting Date is not the same instant, but its components match the
  // target tz when read locally.
  const d = toDate(input);
  const targetTz = tz ?? getResolvedTimezone();
  if (targetTz === 'UTC') return d;
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: targetTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const year = parseInt(get('year'), 10);
  const month = parseInt(get('month'), 10) - 1;
  const day = parseInt(get('day'), 10);
  let hour = parseInt(get('hour'), 10);
  if (hour === 24) hour = 0;
  const minute = parseInt(get('minute'), 10);
  const second = parseInt(get('second'), 10);
  return new Date(year, month, day, hour, minute, second, d.getMilliseconds());
}

export function fromLocalToUtc(localDate: Date, tz?: string): Date {
  // Inverse of toLocalDate: treat the given Date's local components as if they
  // were in `tz`, and return the equivalent UTC Date.
  const targetTz = tz ?? getResolvedTimezone();
  if (targetTz === 'UTC') return new Date(Date.UTC(
    localDate.getFullYear(),
    localDate.getMonth(),
    localDate.getDate(),
    localDate.getHours(),
    localDate.getMinutes(),
    localDate.getSeconds(),
    localDate.getMilliseconds()
  ));
  const utcGuess = Date.UTC(
    localDate.getFullYear(),
    localDate.getMonth(),
    localDate.getDate(),
    localDate.getHours(),
    localDate.getMinutes(),
    localDate.getSeconds(),
    localDate.getMilliseconds()
  );
  const offset = getTzOffsetMs(targetTz, new Date(utcGuess));
  return new Date(utcGuess - offset);
}

function getTzOffsetMs(tz: string, atInstant: Date): number {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = fmt.formatToParts(atInstant);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  let hour = parseInt(get('hour'), 10);
  if (hour === 24) hour = 0;
  const asTz = Date.UTC(
    parseInt(get('year'), 10),
    parseInt(get('month'), 10) - 1,
    parseInt(get('day'), 10),
    hour,
    parseInt(get('minute'), 10),
    parseInt(get('second'), 10)
  );
  return asTz - atInstant.getTime();
}

export interface FormatInTzOptions extends Intl.DateTimeFormatOptions {
  timeZone?: string;
  locale?: string;
}

export function formatInTz(input: DateLike, opts: FormatInTzOptions = {}): string {
  const { locale, ...rest } = opts;
  const tz = rest.timeZone ?? getResolvedTimezone();
  const resolvedLocale = locale ?? getResolvedLocale();
  return new Intl.DateTimeFormat(resolvedLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...rest,
    timeZone: tz
  }).format(toDate(input));
}

export function nowInTz(tz?: string): Date {
  return toLocalDate(new Date(), tz);
}

export function listTimezones(): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  if (typeof supported === 'function') {
    try {
      return supported('timeZone');
    } catch {
      // ignore
    }
  }
  return ['UTC', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'Asia/Tokyo'];
}
